import { ReadonlyVec3, vec3 } from 'gl-matrix';

/**
 * Lowest brightness a preview surface colour may have, as its largest channel.
 *
 * Sized against the two constants that decide whether a surface reads at all:
 * the panel background, whose largest channel is 0.086 (`roi-preview.frag`
 * BACKGROUND), and the shader's ambient floor of 0.22, which is what a surface
 * turned away from the eye keeps. At this value a facing surface lands around
 * 0.39 after shading — plainly visible — while an unlit one sits at the
 * background, which is true of every colour and is what makes the shape read.
 *
 * Brightness here is the largest channel, NOT relative luminance. Rec. 709
 * luminance weights blue at 0.0722 and red at 0.2126, so pure blue and pure
 * red — both ordinary RTSTRUCT colours, perfectly visible on a dark panel —
 * score below any useful luminance floor and would be "corrected" for nothing.
 * What actually makes a colour disappear is every channel being near zero, and
 * that is exactly what the largest channel measures.
 */
export const MIN_SURFACE_VALUE = 0.35;

/** Brightness of a normalised RGB colour: its largest channel. */
export function surfaceValue(color: ReadonlyVec3): number {
    return Math.max(color[0], color[1], color[2]);
}

/**
 * The colour to draw an ROI's preview surface in: the ROI's own colour, lifted
 * only when it is too dark to be seen against the panel.
 *
 * RTSTRUCT colours come from the TPS or the user and are not chosen with this
 * panel in mind — a near-black structure would render as an invisible
 * silhouette on a near-black background, which is worse than the fixed colour
 * this replaced. Brightening is therefore deliberate and bounded:
 *
 * - at or above {@link MIN_SURFACE_VALUE} the colour is returned unchanged,
 *   which is every ordinary clinical colour including pure red and pure blue;
 * - below it, all three channels are scaled by one factor, which lifts the
 *   colour to the floor while leaving its hue and saturation exact;
 * - pure black has no hue to preserve and becomes neutral grey at the floor.
 *
 * @param color RGB normalised to [0, 1] — `RegionOfInterest.color`, NOT the
 *     0-255 `colorRgb8` the same object also carries. A 0-255 colour would sail
 *     past the floor untouched and be clamped to white by the shader, so it is
 *     rejected rather than silently accepted.
 * @returns a new vector; the input is never mutated.
 */
export function previewSurfaceColor(color: ReadonlyVec3): vec3 {
    if (
        !color.every((channel) => Number.isFinite(channel)) ||
        Math.min(color[0], color[1], color[2]) < 0 ||
        Math.max(color[0], color[1], color[2]) > 1
    ) {
        throw new Error(
            `previewSurfaceColor: expected RGB normalised to [0, 1], got ` +
                `[${color[0]}, ${color[1]}, ${color[2]}]`
        );
    }
    const value = surfaceValue(color);
    if (value >= MIN_SURFACE_VALUE) {
        return vec3.clone(color);
    }
    if (value <= 0) {
        return vec3.fromValues(
            MIN_SURFACE_VALUE,
            MIN_SURFACE_VALUE,
            MIN_SURFACE_VALUE
        );
    }
    // Scaling every channel by the same factor moves the colour along its own
    // ray from black, so hue and saturation are untouched and the largest
    // channel lands exactly on the floor — never above it, so never clipped.
    return vec3.scale(vec3.create(), color, MIN_SURFACE_VALUE / value);
}
